import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import { useEffect, useRef, useState, CSSProperties } from "react";
import { gsap } from "gsap";
import { getRequest } from "../../api/apiServices";
import { getDashboardData } from "../../api/apiList";
import Loader from "../../utils/Loading";
import { DashboardCount } from "../../utils/DashboardDataType";
import { useNavigate } from "react-router-dom";
import { roleBasedRedirection } from "../../utils/helper";
import { useAuth } from "../../hooks/useAuth";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BarChartIcon from "@mui/icons-material/BarChart";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CampaignIcon from "@mui/icons-material/Campaign";
import "../../styles/Dashboard/dashboard.css";

type StatCard = {
  title: string;
  count: number;
  icon: React.ReactNode;
  accentColor: string;
  iconBg: string;
  iconColor: string;
  subtitle: string;
  path?: string;
};

const Dashboard = () => {
  const [count, setCount] = useState<DashboardCount>();
  const [isLoading, setIsLoading] = useState(true);
  const heroRef = useRef(null);
  const navigate = useNavigate();
  const gridRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { user } = useAuth();

  const getDashboardCount = async () => {
    setIsLoading(true);
    const response = await getRequest(getDashboardData);
    if (response?.data?.success) {
      setCount(response?.data.data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getDashboardCount();
  }, []);

  useEffect(() => {
    if (isLoading) return;

    const timeline = gsap.timeline({ defaults: { ease: "power3.out" } });

    timeline.fromTo(
      heroRef.current,
      { opacity: 0, y: 24 },
      { opacity: 1, y: 0, duration: 0.7 }
    );

    timeline.fromTo(
      gridRefs.current.filter(Boolean),
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.5, stagger: 0.12 },
      "-=0.3"
    );
  }, [isLoading]);

  const handleNavigation = (path?: string) => {
    if (path) navigate(path);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const statsCards: StatCard[] = [];

  if (user?.status !== 1) {
    statsCards.push({
      title: "Hiring Managers",
      count: count?.hirringManagerCount || 0,
      icon: <PeopleIcon sx={{ fontSize: 26 }} />,
      accentColor: "#345e83",
      iconBg: "rgba(52, 94, 131, 0.1)",
      iconColor: "#345e83",
      subtitle: "Active team leads",
    });
  }

  statsCards.push(
    {
      title: "Total Jobs",
      count: count?.jobCount || 0,
      icon: <WorkIcon sx={{ fontSize: 26 }} />,
      accentColor: "#56c6a9",
      iconBg: "rgba(86, 198, 169, 0.12)",
      iconColor: "#3da88e",
      subtitle: "Open positions",
      path: roleBasedRedirection("/jobs"),
    },
    {
      title: "Total Candidates",
      count: count?.candidateCount || 0,
      icon: <PersonIcon sx={{ fontSize: 26 }} />,
      accentColor: "#244565",
      iconBg: "rgba(36, 69, 101, 0.08)",
      iconColor: "#244565",
      subtitle: "In your pipeline",
      path: roleBasedRedirection("/candidates"),
    }
  );

  const quickActions = [
    {
      label: "Post a Job",
      icon: <AddCircleOutlineIcon sx={{ fontSize: 22, color: "#56c6a9" }} />,
      path: roleBasedRedirection("/jobs/create-jobprofile"),
    },
    {
      label: "View Candidates",
      icon: <PersonIcon sx={{ fontSize: 22, color: "#244565" }} />,
      path: roleBasedRedirection("/candidates"),
    },
    {
      label: "Campaigns",
      icon: <CampaignIcon sx={{ fontSize: 22, color: "#345e83" }} />,
      path: roleBasedRedirection("/campaigns"),
    },
  ];

  const totalPipeline =
    (count?.jobCount || 0) +
    (count?.candidateCount || 0) +
    (user?.status !== 1 ? count?.hirringManagerCount || 0 : 0);

  return isLoading ? (
    <Loader />
  ) : (
    <Box className="dashboard-page">
      {/* Hero */}
      <Box ref={heroRef} className="dashboard-hero" sx={{ mb: 4 }}>
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box className="dashboard-hero-badge">
            <TrendingUpIcon sx={{ fontSize: 14 }} />
            Recruitment Hub
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "26px", sm: "30px", md: "34px" },
              mb: 1,
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            {getGreeting()}, {user?.name || "User"}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              opacity: 0.85,
              fontSize: { xs: "14px", sm: "16px" },
              maxWidth: 520,
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.9)",
            }}
          >
            Track your hiring pipeline, manage open roles, and connect with
            top talent — all in one place.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mt: 3,
            }}
          >
            <Chip
              label={`${count?.jobCount || 0} Active Jobs`}
              sx={{
                background: "rgba(132, 225, 204, 0.18)",
                color: "#84e1cc",
                fontWeight: 600,
                fontSize: "13px",
                border: "1px solid rgba(132, 225, 204, 0.3)",
              }}
            />
            <Chip
              label={`${count?.candidateCount || 0} Candidates`}
              sx={{
                background: "rgba(255, 255, 255, 0.1)",
                color: "rgba(255,255,255,0.9)",
                fontWeight: 600,
                fontSize: "13px",
                border: "1px solid rgba(255, 255, 255, 0.15)",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((item, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={user?.status !== 1 ? 4 : 6}
            key={item.title}
            ref={(el) => (gridRefs.current[index] = el)}
          >
            <Card
              className={`dashboard-stat-card${item.path ? " clickable" : ""}`}
              style={
                {
                  "--accent-color": item.accentColor,
                  "--icon-bg": item.iconBg,
                  "--icon-color": item.iconColor,
                } as CSSProperties
              }
              onClick={() => item.path && handleNavigation(item.path)}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2.5,
                  }}
                >
                  <Box className="dashboard-stat-icon">{item.icon}</Box>
                  {item.path && (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(132, 225, 204, 0.1)",
                        color: "#56c6a9",
                        transition: "all 0.3s ease",
                        ".dashboard-stat-card.clickable:hover &": {
                          background: "#84e1cc",
                          color: "#244565",
                        },
                      }}
                    >
                      <ArrowForwardIcon sx={{ fontSize: 18 }} />
                    </Box>
                  )}
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    color: "#273848",
                    fontWeight: 800,
                    fontSize: { xs: "36px", sm: "42px" },
                    mb: 0.5,
                    lineHeight: 1,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {item.count}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: "#273848",
                    fontWeight: 700,
                    fontSize: { xs: "15px", sm: "17px" },
                    mb: 0.5,
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: { xs: "12px", sm: "13px" },
                    fontWeight: 500,
                  }}
                >
                  {item.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mb: 4, position: "relative", zIndex: 1 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "15px",
            color: "#273848",
            mb: 2,
            letterSpacing: "-0.01em",
          }}
        >
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          {quickActions.map((action) => (
            <Grid item xs={12} sm={4} key={action.label}>
              <Box
                className="dashboard-quick-action"
                onClick={() => handleNavigation(action.path)}
              >
                {action.icon}
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#273848",
                    flex: 1,
                  }}
                >
                  {action.label}
                </Typography>
                <ArrowForwardIcon
                  sx={{ fontSize: 16, color: "#94a3b8" }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Overview */}
      <Card className="dashboard-overview-card">
        <Box className="dashboard-overview-header">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #56c6a9, #84e1cc)",
              }}
            >
              <BarChartIcon sx={{ fontSize: 22, color: "#244565" }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#273848",
                  fontSize: { xs: "17px", sm: "19px" },
                  letterSpacing: "-0.01em",
                }}
              >
                Recruitment Overview
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#64748b", fontSize: "13px" }}
              >
                {totalPipeline} total records across your workspace
              </Typography>
            </Box>
          </Box>
        </Box>

        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={4}>
              <Box className="dashboard-metric-tile">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(86, 198, 169, 0.12)",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <WorkIcon sx={{ fontSize: 26, color: "#56c6a9" }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#273848",
                    fontWeight: 800,
                    fontSize: { xs: "28px", sm: "32px" },
                    mb: 0.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {count?.jobCount || 0}
                </Typography>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Jobs Posted
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box className="dashboard-metric-tile">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(36, 69, 101, 0.08)",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 26, color: "#244565" }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#273848",
                    fontWeight: 800,
                    fontSize: { xs: "28px", sm: "32px" },
                    mb: 0.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {count?.candidateCount || 0}
                </Typography>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  Total Candidates
                </Typography>
              </Box>
            </Grid>

            {user?.status !== 1 ? (
              <Grid item xs={12} sm={4}>
                <Box className="dashboard-metric-tile">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(52, 94, 131, 0.1)",
                      mx: "auto",
                      mb: 1.5,
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 26, color: "#345e83" }} />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#273848",
                      fontWeight: 800,
                      fontSize: { xs: "28px", sm: "32px" },
                      mb: 0.5,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {count?.hirringManagerCount || 0}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    Hiring Managers
                  </Typography>
                </Box>
              </Grid>
            ) : (
              <Grid item xs={12} sm={4}>
                <Box
                  className="dashboard-metric-tile"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 140,
                    background:
                      "linear-gradient(135deg, #f0fdf9 0%, #ffffff 100%)",
                    border: "1px dashed rgba(132, 225, 204, 0.5)",
                  }}
                >
                  <TrendingUpIcon
                    sx={{ fontSize: 32, color: "#84e1cc", mb: 1 }}
                  />
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontWeight: 600,
                      fontSize: "13px",
                      textAlign: "center",
                      maxWidth: 200,
                    }}
                  >
                    Click the stat cards above to explore your data
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
